import type { AssistantAction, AssistantFinding, AssistantMessage, FindingStatus } from './api';
import { actionLabel, isSupportedAction, reportText } from './actions';
import { STATUS_COLORS } from './findings-overlay';
import { reportSendMode } from './radiologist/switches';

/**
 * How one conversation entry looks: the text, the trace line (model + version, what was analysed),
 * the suggested findings with their review buttons, and the action buttons. Text only — nothing
 * from the server is ever parsed as HTML.
 */

export const MESSAGE_TEXT = {
  testProvider: 'Proveedor de prueba, resultado no clínico',
  findingsTitle: 'Hallazgos sugeridos',
  accept: 'Aceptar',
  correct: 'Corregir',
  reject: 'Descartar',
  undo: 'Deshacer',
  jump: 'Ir al corte del hallazgo',
  local: 'Respuesta desde la cabecera DICOM de la imagen actual (sin enviar datos).',
  model: 'Modelo',
  version: 'versión',
  reportPreview: 'Texto que se copiará:',
  reportPreviewSend: 'Texto que se enviará al informe:',
  followup: 'Seguimiento',
  followupTitle: 'Seguimiento recomendado por guía para este hallazgo',
  keyImage: 'Imagen clave',
  keyImageTitle: 'Guardar la imagen de este hallazgo como imagen clave',
  showAll: 'ver todo',
  showLess: 'ver menos',
  escalate: 'Escalar como crítico',
  escalated: 'Escalado al módulo de hallazgos críticos',
  slices: 'cortes',
} as const;

/** Characters of a suggested report text shown before "ver todo". */
export const REPORT_PREVIEW_CHARS = 240;

const STATUS_LABELS: Record<FindingStatus, string> = {
  pending: 'Pendiente de revisión',
  accepted: 'Aceptado',
  corrected: 'Corregido',
  rejected: 'Descartado',
};

const SEVERITY_LABELS = { routine: '', urgent: 'Urgente', critical: 'Posible crítico' } as const;
const PERCENT = 100;

export interface MessageHandlers {
  /** The session may decide findings (viewer:ai:review). */
  canReview: boolean;
  onJump(finding: AssistantFinding): void;
  onDecide(finding: AssistantFinding, decision: Exclude<FindingStatus, 'pending'>): void;
  onUndo(finding: AssistantFinding): void;
  onAction(action: AssistantAction): void;
  /** "Escalar como crítico" (an accepted / corrected critical finding); absent = no button. */
  onEscalate?(finding: AssistantFinding): void;
  /** Findings already escalated in this session (their button reads as done). */
  isEscalated?(finding: AssistantFinding): boolean;
  /** "Seguimiento" on an accepted / corrected finding (radiologist tools §2.6); absent = no button. */
  onFollowup?(finding: AssistantFinding): void;
  /** "Imagen clave" on an accepted / corrected finding with a location (§2.8); absent = no button. */
  onKeyImage?(finding: AssistantFinding): void;
  /** Whether the org switched that tool on and the session may use it (read once at install). */
  toolShown?(tool: 'followups' | 'key_images'): boolean;
}

function el<K extends keyof HTMLElementTagNameMap>(tag: K, className: string, text?: string): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

function button(label: string, className: string, onClick: () => void, title?: string): HTMLButtonElement {
  const b = el('button', className, label);
  b.type = 'button';
  if (title) b.title = title;
  b.addEventListener('click', onClick);
  return b;
}

function traceLine(message: AssistantMessage): HTMLElement | null {
  const parts: string[] = [];
  if (message.model) parts.push(`${MESSAGE_TEXT.model}: ${message.model}${message.modelVersion ? ` · ${MESSAGE_TEXT.version} ${message.modelVersion}` : ''}`);
  if (message.analysed?.note) parts.push(message.analysed.note);
  else if (message.analysed?.images) parts.push(`${message.analysed.images} imágenes analizadas`);
  if (message.analysed && message.analysed.series.length > 0) parts.push(message.analysed.series.join(', '));
  return parts.length > 0 ? el('div', 'assistant-trace', parts.join(' · ')) : null;
}

/** "cortes 40–46" for a finding that spans slices (1-based, as the radiologist counts them). */
export function sliceRange(finding: AssistantFinding): string | null {
  const start = finding.location?.index;
  const end = finding.location?.index_end;
  if (start === null || start === undefined || end === null || end === undefined || end <= start) return null;
  return `${MESSAGE_TEXT.slices} ${start + 1}–${end + 1}`;
}

function findingHead(finding: AssistantFinding): string {
  const parts = [finding.label];
  const range = sliceRange(finding);
  if (range) parts.push(range);
  if (finding.laterality) parts.push(finding.laterality);
  if (finding.confidence !== null) parts.push(`${Math.round(finding.confidence * PERCENT)} %`);
  return parts.join(' · ');
}

function reviewButtons(finding: AssistantFinding, handlers: MessageHandlers): HTMLElement | null {
  if (!handlers.canReview) return null;
  const row = el('div', 'assistant-finding-actions');
  const escalate = escalateButton(finding, handlers);
  if (finding.status === 'pending') {
    row.append(
      button(MESSAGE_TEXT.accept, 'assistant-btn assistant-accept', () => handlers.onDecide(finding, 'accepted')),
      button(MESSAGE_TEXT.correct, 'assistant-btn assistant-correct', () => handlers.onDecide(finding, 'corrected')),
      button(MESSAGE_TEXT.reject, 'assistant-btn assistant-reject', () => handlers.onDecide(finding, 'rejected')),
    );
  } else {
    row.append(button(MESSAGE_TEXT.undo, 'assistant-btn assistant-undo', () => handlers.onUndo(finding)));
  }
  if (escalate) row.appendChild(escalate);
  return row;
}

/** Only for a critical finding a person accepted or corrected; the module asks for confirmation first. */
function escalateButton(finding: AssistantFinding, handlers: MessageHandlers): HTMLButtonElement | null {
  const onEscalate = handlers.onEscalate;
  if (!onEscalate || finding.severity !== 'critical') return null;
  if (finding.status !== 'accepted' && finding.status !== 'corrected') return null;
  if (handlers.isEscalated?.(finding)) {
    const done = button(MESSAGE_TEXT.escalated, 'assistant-btn assistant-escalate escalated', () => undefined);
    done.disabled = true;
    return done;
  }
  return button(MESSAGE_TEXT.escalate, 'assistant-btn assistant-escalate', () => onEscalate(finding));
}

/**
 * "Seguimiento" and "Imagen clave" on a finding a person accepted or corrected, each only when its
 * tool is on. Not tied to viewer:ai:review: asking costs nothing, and creating a follow-up asks again.
 */
export function findingToolButtons(finding: AssistantFinding, handlers: MessageHandlers): HTMLElement | null {
  if (finding.status !== 'accepted' && finding.status !== 'corrected') return null;
  const shown = (tool: 'followups' | 'key_images') => handlers.toolShown?.(tool) === true;
  const row = el('div', 'assistant-finding-actions assistant-finding-tools');
  const { onFollowup, onKeyImage } = handlers;
  if (onFollowup && shown('followups')) {
    row.appendChild(button(MESSAGE_TEXT.followup, 'assistant-btn assistant-followup', () => onFollowup(finding), MESSAGE_TEXT.followupTitle));
  }
  if (onKeyImage && finding.location && shown('key_images')) {
    row.appendChild(button(MESSAGE_TEXT.keyImage, 'assistant-btn assistant-key-image', () => onKeyImage(finding), MESSAGE_TEXT.keyImageTitle));
  }
  return row.childElementCount > 0 ? row : null;
}

/** One suggested finding: jump target, status, and Aceptar / Corregir / Descartar (or Deshacer). */
export function renderFinding(finding: AssistantFinding, handlers: MessageHandlers): HTMLElement {
  const card = el('div', `assistant-finding status-${finding.status}`);
  card.setAttribute('role', 'listitem');
  card.dataset.findingId = finding.id;
  card.dataset.status = finding.status;
  card.style.setProperty('--finding-color', STATUS_COLORS[finding.status]);
  const head = el('div', 'assistant-finding-head');
  const swatch = el('span', 'assistant-finding-swatch');
  swatch.setAttribute('aria-hidden', 'true');
  const title = finding.location
    ? button(findingHead(finding), 'assistant-finding-jump', () => handlers.onJump(finding), MESSAGE_TEXT.jump)
    : el('span', 'assistant-finding-title', findingHead(finding));
  head.append(swatch, title);
  if (finding.severity !== 'routine') head.appendChild(el('span', `assistant-severity severity-${finding.severity}`, SEVERITY_LABELS[finding.severity]));
  card.appendChild(head);
  if (finding.description) card.appendChild(el('p', 'assistant-finding-desc', finding.description));
  const status = finding.status === 'corrected' && finding.correction ? `${STATUS_LABELS.corrected}: ${finding.correction}` : STATUS_LABELS[finding.status];
  card.appendChild(el('div', 'assistant-finding-status', status));
  const review = reviewButtons(finding, handlers);
  if (review) card.appendChild(review);
  const tools = findingToolButtons(finding, handlers);
  if (tools) card.appendChild(tools);
  return card;
}

/** The model-written text an insert_report button would send or copy, visible (textContent only), truncated with "ver todo". */
export function renderReportPreview(value: string): HTMLElement {
  const box = el('div', 'assistant-report-preview');
  const title = reportSendMode() === 'send' ? MESSAGE_TEXT.reportPreviewSend : MESSAGE_TEXT.reportPreview;
  box.appendChild(el('div', 'assistant-report-preview-title', title));
  const body = el('div', 'assistant-report-preview-text');
  box.appendChild(body);
  if (value.length <= REPORT_PREVIEW_CHARS) {
    body.textContent = value;
    return box;
  }
  const short = `${value.slice(0, REPORT_PREVIEW_CHARS).trimEnd()}…`;
  let expanded = false;
  const toggle = button(MESSAGE_TEXT.showAll, 'assistant-link assistant-report-toggle', () => {
    expanded = !expanded;
    body.textContent = expanded ? value : short;
    toggle.textContent = expanded ? MESSAGE_TEXT.showLess : MESSAGE_TEXT.showAll;
    toggle.setAttribute('aria-expanded', String(expanded));
  });
  toggle.setAttribute('aria-expanded', 'false');
  body.textContent = short;
  box.appendChild(toggle);
  return box;
}

function renderActions(actions: AssistantAction[], handlers: MessageHandlers): HTMLElement | null {
  const supported = actions.filter(isSupportedAction);
  if (supported.length === 0) return null;
  const wrap = el('div', 'assistant-actions-wrap');
  const row = el('div', 'assistant-actions');
  wrap.appendChild(row);
  for (const action of supported) {
    const b = button(actionLabel(action), 'assistant-btn assistant-action', () => handlers.onAction(action));
    b.dataset.action = action.type;
    if (action.type === 'insert_report' && typeof action.args.section === 'string') b.dataset.section = action.args.section;
    row.appendChild(b);
    const preview = reportText(action);
    if (preview) wrap.appendChild(renderReportPreview(preview));
  }
  return wrap;
}

/**
 * The tool switches arrived after some answers were drawn: their insert_report buttons and previews
 * now say "Enviar al informe" / "enviará" when that is what a click will do.
 */
export function refreshReportLabels(root: HTMLElement): void {
  root.querySelectorAll<HTMLButtonElement>('.assistant-action[data-action="insert_report"]').forEach((b) => {
    b.textContent = actionLabel({ type: 'insert_report', label: '', args: { section: b.dataset.section } });
  });
  const title = reportSendMode() === 'send' ? MESSAGE_TEXT.reportPreviewSend : MESSAGE_TEXT.reportPreview;
  root.querySelectorAll('.assistant-actions-wrap .assistant-report-preview-title').forEach((t) => {
    t.textContent = title;
  });
}

/** One entry of the conversation. `local` marks an answer built in the browser from the header. */
export function renderMessage(message: AssistantMessage, handlers: MessageHandlers, local = false): HTMLElement {
  const item = el('div', `assistant-msg assistant-msg-${message.role}`);
  item.dataset.role = message.role;
  if (message.mode) item.dataset.mode = message.mode;
  if (message.isTest) item.appendChild(el('div', 'assistant-test-label', MESSAGE_TEXT.testProvider));
  item.appendChild(el('div', 'assistant-msg-text', message.content));
  if (local) item.appendChild(el('div', 'assistant-trace', MESSAGE_TEXT.local));
  const trace = traceLine(message);
  if (trace) item.appendChild(trace);
  if (message.findings.length > 0) {
    item.appendChild(el('div', 'assistant-findings-title', MESSAGE_TEXT.findingsTitle));
    const findings = el('div', 'assistant-findings');
    findings.setAttribute('role', 'list');
    message.findings.forEach((f) => findings.appendChild(renderFinding(f, handlers)));
    item.appendChild(findings);
  }
  const actions = renderActions(message.actions, handlers);
  if (actions) item.appendChild(actions);
  return item;
}
